import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import ModalContainer from 'design-v2/common/ModalContainer';
import IconsService from 'external-services/icons-service/icons.service';
import { IconTypes } from 'external-services/icons-service/icons.types';
import BookingCardContainer from 'booking-card-list/BookingCardContainer/BookingCardContainer';
import { IBooking } from 'shared-types/index';
import GlobalSearchModalService from 'app/services/globalSearchModal.service';
import GlobalSearchErrorState from './GlobalSearchErrorState';
import { DiaryRootState } from 'appRoot/rootReducer';
import { debounce } from 'lodash-es';
import style from './GlobalBookingSearchModal.module.css';
import { dispatch } from 'rxjs/internal/observable/pairs';
import { closeGlobalSearchModal } from 'app/reducers/globalSearchModal/slice';
import { LocaleService } from 'internal-services/locale/locale.service';
import { useAppDispatch } from 'appRoot/Store';
import { isBookingCreator } from 'app/reducers/settings/slice';
import BookingLookupService from 'external-services/booking-lookup-service';
import PaymentService from 'external-services/diary-payment-service';
import { BookingAvailabilityService } from 'external-services/booking-availability-service';
import { editInBookingCreator } from 'app/reducers/booking/slice';
import ViewActionsService from 'app/actions/view.actions';
import BookingNormaliseService from 'external-services/booking-normalise-service/bookingNormalise.service';
import { cloneDeep } from 'lodash-es';
import { TextField, InputAdornment, makeStyles, Tooltip, ClickAwayListener } from '@material-ui/core';
import SearchIconMUI from '@material-ui/icons/Search';
import color from 'design-v2/globals/colors/colors.module.css';
import classNames from 'classnames';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import RestoreOutlinedIcon from '@material-ui/icons/RestoreOutlined';

export interface IBookingSearchResult extends IBooking {
    // Additional properties specific to search results can be added here
}

export interface IGlobalBookingSearchModalProps {
    isOpen: boolean;
    onRequestClose: () => void;
    filterDate?: Date;
}

export enum FilterType {
    Future = 'future',
    Current = 'current',
    All = 'all'
}

const RECENT_SEARCH_LIMIT = 5;
const RECENT_SEARCH_KEY = 'recent_search';

const useStyles = makeStyles({
    textField: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#d0d0d0',
            },
            '&:hover fieldset': {
                borderColor: '#b0b0b0',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#992662',
                borderWidth: '2px',
            },
        },
        '& .MuiInputBase-input': {
            fontSize: '16px',
        },
    },
    tooltip: {
        backgroundColor: '#4a4a4a',
        color: '#ffffff',
        fontSize: '13px',
        padding: '12px 16px',
        borderRadius: '4px',
        lineHeight: '1.6',
        maxWidth: 220,
    },
    arrow: {
        color: '#4a4a4a',
    },
});

export default function GlobalBookingSearchModal({
    isOpen,
    onRequestClose,
    filterDate
}: IGlobalBookingSearchModalProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState<FilterType>(FilterType.Future);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [recentSearch, setRecentSearch] = useState([]);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const resultsContainerRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const classes = useStyles();
    const { bookingsByType } = useSelector((state: DiaryRootState) => state.filterBookingReducer);
    const handleBookingClicked = (activeBookingId: string) => {// no need to handle this
        return;
    }

    // Get state from Redux
    const globalSearchModal = useSelector((state: DiaryRootState) => state.globalSearchModalReducer);
    const {
        searchResults,
        loading,
        moreLoading,
        loadError,
        bookmark
    } = globalSearchModal;

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce((query: string, filter: FilterType) => {
            if (query.trim()) {
                GlobalSearchModalService.searchBookings(query, filter, filterDate);
                addRecentSearch(query);
            } else {
                GlobalSearchModalService.clearSearchResults();
            }
        }, 900),
        [filterDate]
    );
    const addRecentSearch = (query: string) => {
        const lastSearch = {
            value: query,
            date: new Date()
        }
        if (recentSearch.length >= RECENT_SEARCH_LIMIT) {
            recentSearch.pop();
        }
        recentSearch.unshift(lastSearch);
        localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(recentSearch));
    }
    const getDateDisplayStr = (date: Date) => {
        const now = new Date();
        const diffTime = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return 'today';
        } else if (diffDays === 1) {
            return 'yesterday';
        } else if (diffDays < 30) {
            return `${diffDays} days ago`;
        } else {
            return date.toLocaleDateString(LocaleService.getLocale(), {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }
    }
    // Auto-focus on search input when modal opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
        setSearchQuery('');
    }, [isOpen]);

    // Load more results handler
    const handleLoadMore = () => {
        if (bookmark && !moreLoading && searchQuery.trim()) {
            GlobalSearchModalService.loadMoreResults(searchQuery);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        debouncedSearch(query, activeFilter);
    };


    const handleFilterChange = (filter: FilterType) => {
        setActiveFilter(filter);

        // Re-search with new filter if there's a query
        if (searchQuery.trim()) {
            debouncedSearch(searchQuery, filter);
        }
    };

    const handleRetrySearch = () => {
        if (searchQuery.trim()) {
            GlobalSearchModalService.searchBookings(searchQuery, activeFilter, filterDate);
        }
    };

    const handleRecentSearchClick = (search: string) => {
        setSearchQuery(search);
        debouncedSearch(search, activeFilter);
    };

    const handleBookingClick = (booking: IBookingSearchResult) => {
        const activeBooking = BookingNormaliseService.normalizeModelToView(cloneDeep(booking as any));
        // onBookingSelect?.(booking);
        onRequestClose();
        const isLocked = PaymentService.conditionallyShowLockedBookingDialog(activeBooking);
        if (isLocked) {
            return;
        }
        if (isBookingCreator()) {
            BookingLookupService.getById(activeBooking._id, activeBooking.time).then((booking) => {
                /**
             * There is a property called 'tablesAvaiable' that gets added to WL bookings on bookingsByType, inside the
             * BookingListFilterService, but it does not get saved to the booking. So here we need look up the value and
             * attach it to the active booking again when opening it. It is responsible for displaying the "Seat Now"
             * button on the WaitList booking summary.
             */
            BookingAvailabilityService.addTablesAvailableFlagToCleanBooking(
                booking,
                bookingsByType?.waitlist
            );
                dispatch(editInBookingCreator({ activeBooking: booking }));
            })
            

            
        } else {
            ViewActionsService.onEditBooking({
                booking: activeBooking,
            });
        }
    };

    const SearchIcon = IconsService.getIcon(IconTypes.personSearch);
    // const CloseIcon = IconsService.getIcon(IconTypes.close); 
    const InfoIcon = IconsService.getIcon(IconTypes.warning);
    const ClockIcon = IconsService.getIcon(IconTypes.scheduleIcon);
    useEffect(() => {
        const recentSearch = localStorage.getItem(RECENT_SEARCH_KEY);
        if (recentSearch) {
            setRecentSearch(JSON.parse(recentSearch));
        }
        setSearchQuery('');
    }, []);
    return (
        <ModalContainer
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            isMaxWidth={false}
            contentStyle={{
                maxWidth: '600px',
                width: '90%',
                maxHeight: '60vh',
                padding: '24px'
            }}
        >
            <div className={style.modalContent} data-testid="global-booking-search-modal">

                {/* Search Input */}
                <div className={style.searchSection}>
                    <div className={style.searchInputContainer}>
                        <TextField
                            inputRef={searchInputRef}
                            className={classes.textField}
                            fullWidth
                            variant="outlined"
                            label="Search customer or company"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            autoComplete="off"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIconMUI style={{ fontSize: '2.4rem' }} className={classNames({
                                            [color.clrGreyHeavy]: true,
                                        })} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
                                            <Tooltip
                                                title={
                                                    <div>
                                                        <div style={{ fontWeight: 600, marginBottom: '8px' }}>You can search by:</div>
                                                        <ul style={{ margin: 0, paddingLeft: '20px', listStyleType: 'disc' }}>
                                                            <li>First name</li>
                                                            <li>Last name</li>
                                                            <li>Company</li>
                                                            <li>Email</li>
                                                            <li>Phone number</li>
                                                        </ul>
                                                    </div>
                                                }
                                                arrow
                                                placement="top"
                                                classes={{
                                                    tooltip: classes.tooltip,
                                                    arrow: classes.arrow,
                                                }}
                                                open={tooltipOpen}
                                                onOpen={() => setTooltipOpen(true)}
                                                onClose={() => setTooltipOpen(false)}
                                            >
                                                <InfoOutlinedIcon
                                                    style={{ fontSize: '2.4rem', cursor: 'pointer' }}
                                                    className={classNames({
                                                        [color.clrGreyMedium]: true,
                                                    })}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setTooltipOpen(prev => !prev);
                                                    }}
                                                />
                                            </Tooltip>
                                        </ClickAwayListener>
                                    </InputAdornment>
                                ),
                                style: {
                                    padding: '0 !important'
                                }
                            }}
                        />
                        <button
                            className={style.clearButton}
                            onClick={onRequestClose}
                            type="button"
                        >
                            <CloseOutlinedIcon className={style.closeIcon} />
                        </button>
                    </div>
                </div>

                {/* Filter Buttons */}
                <div className={style.filterSection}>
                    <button
                        className={`${style.filterButton} ${activeFilter === FilterType.Future ? style.filterButtonActive : ''}`}
                        onClick={() => handleFilterChange(FilterType.Future)}
                        type="button"
                    >
                        Future bookings
                    </button>
                    <button
                        className={`${style.filterButton} ${activeFilter === FilterType.Current ? style.filterButtonActive : ''}`}
                        onClick={() => handleFilterChange(FilterType.Current)}
                        type="button"
                    >
                        Current day
                    </button>
                    <button
                        className={`${style.filterButton} ${activeFilter === FilterType.All ? style.filterButtonActive : ''}`}
                        onClick={() => handleFilterChange(FilterType.All)}
                        type="button"
                    >
                        All bookings
                    </button>
                </div>

                {/* Divider */}
                <div style={{ height: '1px', backgroundColor: '#f5f5f5', width: '100%' }}></div>

                {/* Search Results */}
                {!!searchQuery ? <div className={style.resultsSection}>
                    <p  data-testid="search-results-title"
                        className={style.resultsTitle}>
                        Search results
                    </p>
                    <div
                        ref={resultsContainerRef}
                        className={style.resultsList}
                    >
                        {/* Loading State */}
                        {loading && (
                            <div className={style.loadingState}>
                                <div className={style.loadingSpinner}></div>
                                <div className={style.loadingText}>Searching...</div>
                            </div>
                        )}

                        {/* Error State */}
                        {loadError && !loading && (
                            <GlobalSearchErrorState onRetry={handleRetrySearch} />
                        )}

                        {/* No Results State */}
                        {!loading && !loadError && searchResults.length === 0 && searchQuery.trim() && (
                            <div className={style.noResultsState}>
                                <div className={style.noResultsIcon}>
                                    <SearchIcon />
                                </div>
                                <h3 className={style.noResultsTitle}>No Results Found</h3>
                                <p className={style.noResultsMessage}>Please check your search settings</p>
                            </div>
                        )}

                        {/* Search Results */}
                        {!loading && !loadError && searchResults.length > 0 && (
                            <>
                                {searchResults.map((booking) => (
                                    <div
                                        key={booking._id}
                                        className={style.bookingCardWrapper}
                                        onClick={() => handleBookingClick(booking)}
                                    >
                                        <BookingCardContainer
                                            booking={booking}
                                            handleBookingClicked={handleBookingClicked}
                                            showDateInBookingCard={true}
                                            useDeprecatedStatusModal={true}
                                            isEditable={false}
                                        />
                                    </div>
                                ))}

                                {/* Load More Button */}
                                {bookmark && !moreLoading && (
                                    <div className={style.loadMoreContainer}>
                                        <button
                                            className={style.loadMoreButton}
                                            onClick={handleLoadMore}
                                            type="button"
                                        >
                                            Load More
                                        </button>
                                    </div>
                                )}

                                {/* More Loading State */}
                                {moreLoading && (
                                    <div className={style.loadingState}>
                                        <div className={style.loadingSpinner}></div>
                                        <div className={style.loadingText}>Loading more...</div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div> : <div className={style.recentSearchSection}>
                    <p className={style.recentSearchTitle}>Recent searches</p>
                    <div className={style.recentSearchList}>
                        {recentSearch.map((search, index) => (
                            <div key={`${search.value}-${index}`} className={style.recentSearchItem} onClick={() => handleRecentSearchClick(search.value)}>
                                <div className={style.recentSearchIconWrapper}>
                                    <RestoreOutlinedIcon className={style.recentSearchIcon} />
                                </div>
                                <div className={style.recentSearchContent}>
                                    <span className={style.recentSearchValue}>{search.value}</span>
                                    <span className={style.recentSearchTime}>{getDateDisplayStr(new Date(search.date))}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>}
            </div>
        </ModalContainer>
    );
}
